import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Collection } from '@sorare/core/src/atoms/icons/Collection';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { isType, isTypes } from '@sorare/core/src/lib/gql';
import type { HexColor } from '@sorare/core/src/style/types.ts';

import { ProgressTaskCard } from '#src/components/missions/ProgressTaskCard/index.tsx';
import { getTaskCompetitionColor } from '#src/lib/task/index.tsx';

import type { SetCollectionTask_taskInterface } from './__generated__/index.graphql.ts';

type Props = {
  task: SetCollectionTask_taskInterface;
  onClaim: () => void;
  onClick: (() => void) | null;
  loading: boolean;
  selected?: boolean;
};

export const SetCollectionTask = ({
  task: taskTrack,
  onClaim,
  loading,
  onClick,
  selected,
}: Props) => {
  const task = isType(taskTrack, 'TasksTrack') ? taskTrack.currentStep : null;
  if (
    !isTypes(task, [
      'CardsCountTargetTask',
      'CompleteCollectionsCountTargetTask',
    ])
  ) {
    return null;
  }

  const { rewardConfigs, progress, target } = task;
  const rewardConfig = rewardConfigs.at(0);

  const taskSetColor = ((isType(task, 'CardsCountTargetTask')
    ? task.cardSet?.branding?.colors?.surfacePrimary
    : null) ?? 'var(--c-nd-600-opaque)') as HexColor;

  const competitionsDisplayName =
    task.competitions.length > 0
      ? task.competitions.map(({ displayName }) => displayName).join(', ')
      : '';
  const leagueName =
    competitionsDisplayName ||
    (isType(task, 'CardsCountTargetTask') && task.cardSet?.name);
  const accentColor = getTaskCompetitionColor(task, taskSetColor);

  return (
    <ProgressTaskCard
      task={task}
      category={leagueName}
      description={
        <Vertical gap={0}>
          <LabelL bold>{task.title}</LabelL>
          <LabelL>{task.description}</LabelL>
        </Vertical>
      }
      accentColor={accentColor}
      titleColor={accentColor}
      progress={progress}
      target={target}
      rewardConfig={rewardConfig}
      progressTrailing={
        isType(task, 'CardsCountTargetTask') ? (
          <ScarcityIcon
            rarity={task.rarity ?? Rarity.common}
            cardSet={task.cardSet?.slug}
          />
        ) : (
          <Collection size={2} />
        )
      }
      onClaim={onClaim}
      loading={loading}
      onClick={onClick}
      selected={selected}
    />
  );
};

SetCollectionTask.fragments = {
  taskInterface: gql`
    fragment SetCollectionTask_taskInterface on TaskInterface {
      id
      aasmState
      ... on TasksTrack {
        id
        currentStep {
          id
          title
          description
          rewardConfigs {
            id
            ...ProgressTaskCard_anyRewardConfigInterface
          }
          ... on CompleteCollectionsCountTargetTask {
            id
            progress
            target
            competitions {
              slug
              displayName
            }
          }
          ... on CardsCountTargetTask {
            id
            progress
            target
            rarity
            cardSet {
              slug
              name
              branding {
                id
                colors {
                  surfacePrimary
                }
              }
            }
            competitions {
              slug
              displayName
            }
          }
          ...getTaskCompetitionColor_taskInterface
          ...ProgressTaskCard_taskInterface
        }
      }
    }
    ${ProgressTaskCard.fragments.taskInterface}
    ${ProgressTaskCard.fragments.anyRewardConfigInterface}
    ${getTaskCompetitionColor.fragments.taskInterface}
  ` as TypedDocumentNode<SetCollectionTask_taskInterface>,
};
